import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Edit2, Trash2, Plus } from 'lucide-react';
import { userService } from '../../_services';

function AddWholesaler({ titleText, activePagin, openWholesalerPopup, closeAddWholesalerPopup }) {
    const [loading, setLoading] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const [hubId, setHubId] = useState('');
    const [whName, setWhName] = useState('');
    const [hubError, setHubError] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [commissionMilestone, setCommissionMilestone] = useState([]);
    const [commissionRules, setCommissionRules] = useState('yes');
    const [enableGlic, setEnableGlic] = useState(false);
    const [enableTripRx, setEnableTripRx] = useState(false);
    const [customGroupSequence, setCustomGroupSequence] = useState('no');
    const [createDashboard, setCreateDashboard] = useState(true);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = (response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch (error) {
                return [];
            }
        });
    };

    const hubIdSubmit = useCallback(async () => {
        if (!hubId || hubId === '') {
            setHubError('Hubspot Company Id required');
            return;
        }

        setLoading(true);
        setHubError('');
        let keepLoading = false;

        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    roleName: userdata.paramsname,
                    roleType: userdata.userrole,
                    hubspotid: hubId
                })
            };

            const response = await fetch(`/api/admin/CompanyInfo`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata && apidata.length > 0) {
                // Check if user/company is available
                if (apidata[0].status === "User Available") {
                    // Set wholesaler name from response
                    setWhName(apidata[0].whname);
                    keepLoading = true; // Keep loading for 1.8s delay
                    
                    // Delay before showing form (like production-v3)
                    setTimeout(() => {
                        setShowForm(true);
                        setLoading(false);
                        setHubError('');
                    }, 1800);
                } else {
                    // Show the status as error message
                    setHubError(apidata[0].status || 'Invalid Hubspot Company Id');
                }
            } else {
                setHubError('Whoops, something went wrong. Please try again');
            }
        } catch (error) {
            setHubError('Error fetching Hubspot data');
        } finally {
            // Clear loading immediately for errors, keep it for success (1.8s delay)
            if (!keepLoading) {
                setLoading(false);
            }
        }
    }, [hubId, userdata]);

    const addMilestone = useCallback(() => {
        const randomId = Math.floor(10000 + Math.random() * 90000);
        const newMilestone = {
            msid: '',
            from: '',
            to: '',
            commission: '',
            isineditedmode: true,
            id: randomId
        };
        setCommissionMilestone(prev => [...prev, newMilestone]);
        setError('');
    }, []);

    const saveData = useCallback((index, from, to, commission) => {
        const wholeNumber = /^(?:0|(?:[1-9](?:\d{0,2}(?:,\d{3})+|\d*)))$/;

        if (!userService.Check_Null_Empty(from) && !userService.Check_Null_Empty(to) && 
            !userService.Check_Null_Empty(commission) && userService.validateCurrency(commission)) {
            
            if (wholeNumber.test(from)) {
                if (wholeNumber.test(to)) {
                    if (parseFloat(commission) > 10) {
                        setError('Commission should not be more than 10');
                        return false;
                    } else if (parseFloat(commission) <= 0) {
                        setError('Commission should be greater than 0');
                        return false;
                    } else {
                        if (parseInt(to) > parseInt(from)) {
                            setCommissionMilestone(prev => prev.map((item, idx) => {
                                if (idx === index) {
                                    return {
                                        ...item,
                                        msid: (idx + 1).toString(),
                                        from,
                                        to,
                                        commission,
                                        isineditedmode: false
                                    };
                                }
                                return item;
                            }));
                            setError('');
                        } else {
                            setError('The milestone To fields should be greater than the From fields');
                        }
                    }
                } else {
                    setError('To field must be whole number');
                }
            } else {
                setError('From field must be whole number');
            }
        } else {
            setError('All milestone fields are required and commission must be numeric');
        }
    }, []);

    const handleEdit = useCallback((index) => {
        setCommissionMilestone(prev => prev.map((item, idx) => ({
            ...item,
            isineditedmode: idx === index
        })));
        setError('');
    }, []);

    const deleteMilestone = useCallback((index) => {
        setCommissionMilestone(prev => prev.filter((_, idx) => idx !== index));
        setError('');
    }, []);

    const cancelList = useCallback((index) => {
        setCommissionMilestone(prev => {
            const updated = [...prev];
            if (updated[index].from === '' && updated[index].to === '' && updated[index].commission === '') {
                return updated.filter((_, idx) => idx !== index);
            }
            return updated.map((item, idx) => ({
                ...item,
                isineditedmode: false
            }));
        });
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setBtnLoader(true);
        setError('');
        setSuccessMessage('');

        const formData = new FormData(e.target);
        const wholesalerCommission = formData.get('wholesalerCommission');
        const contactName = formData.get('contactName');
        const email = formData.get('email');

        // Validation
        if (!wholesalerCommission || !contactName || !email) {
            setError('All fields are required');
            setSuccessMessage('');
            setBtnLoader(false);
            return;
        }

        if (!userService.validateCurrency(wholesalerCommission)) {
            setError('Commission must be numeric');
            setSuccessMessage('');
            setBtnLoader(false);
            return;
        }

        if (parseFloat(wholesalerCommission) > 10) {
            setError('Commission should not be more than 10');
            setSuccessMessage('');
            setBtnLoader(false);
            return;
        }

        // Prepare milestones JSON for header (matching old format)
        const milestonesForHeader = commissionMilestone
            .filter(m => !m.isineditedmode)
            .map((m, index) => ({
                msid: String(index + 1),
                from: m.from,
                to: m.to,
                commission: m.commission,
                isineditedmode: false
            }));

        // Prepare data matching backend 'addwh' model
        const wholesalerData = {
            whid: '-1',  // -1 for new wholesaler
            roleName: userdata.paramsname,
            roleType: userdata.userrole,
            whname: whName,
            companyid: hubId,
            whemail: email,
            whcontact: contactName,
            whcomm: wholesalerCommission,
            isruled: commissionRules === 'yes',
            isGlicEnable: enableGlic,
            isdashboardneeded: createDashboard,
            iscustomgroup: customGroupSequence === 'yes',
            gnprefix: '',  // Empty for auto-generation
            isDynamic: false,
            IsTripRx: enableTripRx,
            isprescribeData: false
        };

        try {
            const headers = {
                'Content-Type': 'application/json'
            };

            // Add milestones as MSJsonHeader if commission rules enabled
            if (commissionRules === 'yes' && milestonesForHeader.length > 0) {
                headers['MSJsonHeader'] = JSON.stringify(milestonesForHeader);
            }

            const requestOptions = {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(wholesalerData)
            };

            const response = await fetch(`/api/admin/AddNewWhDetail`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === "1") {
                    setSuccessMessage(apidata[0].statusMessage);
                    setError('');
                    setTimeout(() => {
                        handleCancel();
                    }, 2000);
                } else {
                    setError(apidata[0].statusMessage);
                    setSuccessMessage('');
                }
            } else {
                setError('Error creating wholesaler');
                setSuccessMessage('');
            }
        } catch (error) {
            setError('Error creating wholesaler');
            setSuccessMessage('');
        } finally {
            setBtnLoader(false);
        }
    }, [hubId, whName, commissionRules, enableGlic, enableTripRx, customGroupSequence, createDashboard, commissionMilestone, userdata]);

    const handleCancel = () => {
        closeAddWholesalerPopup(activePagin);
        openWholesalerPopup(activePagin);
    };

    return (
        <Dialog open={true} onOpenChange={handleCancel}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle>{whName || titleText || 'New Wholesaler'}</DialogTitle>
                </DialogHeader>

                {!showForm ? (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Please Enter Hubspot Company Id</Label>
                            <Input
                                type="text"
                                value={hubId}
                                onChange={(e) => {
                                    setHubId(e.target.value);
                                    setHubError('');
                                }}
                            />
                        </div>
                        {hubError && (
                            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                {hubError}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <Button onClick={hubIdSubmit} disabled={loading} className="bg-primary hover:bg-primary/90">
                                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Submit
                            </Button>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Hubspot Company Id</Label>
                                <Input type="text" value={hubId} readOnly className="bg-muted" />
                            </div>
                            <div className="space-y-2">
                                <Label>Wholesaler Name</Label>
                                <Input type="text" value={whName} readOnly className="bg-muted" />
                            </div>
                            <div className="space-y-2">
                                <Label>Commission</Label>
                                <Input type="text" name="wholesalerCommission" required />
                            </div>
                            <div className="space-y-2">
                                <Label>Contact Name</Label>
                                <Input type="text" name="contactName" required />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label>Email</Label>
                                <Input type="email" name="email" required className="md:w-1/2" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="space-y-2">
                                <Label>Commission Rules</Label>
                                <RadioGroup value={commissionRules} onValueChange={setCommissionRules}>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="yes" id="rules-yes" />
                                        <Label htmlFor="rules-yes" className="font-normal cursor-pointer">Yes</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="no" id="rules-no" />
                                        <Label htmlFor="rules-no" className="font-normal cursor-pointer">No</Label>
                                    </div>
                                </RadioGroup>
                            </div>

                            <div className="space-y-2">
                                <Label>Enable GlicRx</Label>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="enableGlic"
                                        checked={enableGlic}
                                        onCheckedChange={setEnableGlic}
                                    />
                                    <Label htmlFor="enableGlic" className="font-normal cursor-pointer">Enable</Label>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Enable TripRx</Label>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="enableTripRx"
                                        checked={enableTripRx}
                                        onCheckedChange={setEnableTripRx}
                                    />
                                    <Label htmlFor="enableTripRx" className="font-normal cursor-pointer">Enable</Label>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Custom Group Sequence?</Label>
                                <RadioGroup value={customGroupSequence} onValueChange={setCustomGroupSequence}>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="yes" id="sequence-yes" />
                                        <Label htmlFor="sequence-yes" className="font-normal cursor-pointer">Yes</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="no" id="sequence-no" />
                                        <Label htmlFor="sequence-no" className="font-normal cursor-pointer">No</Label>
                                    </div>
                                </RadioGroup>
                            </div>
                        </div>

                        {commissionRules === 'yes' && (
                            <div className="space-y-4 border-t pt-4">
                                <h3 className="text-lg font-semibold">Commission Milestones</h3>

                                {commissionMilestone.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                                        <p className="text-muted-foreground">No milestones added yet</p>
                                        <Button type="button" onClick={addMilestone} variant="secondary">
                                            Add Milestone
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-end">
                                            <Button type="button" onClick={addMilestone} variant="secondary" size="sm">
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add Milestone
                                            </Button>
                                        </div>
                                        <div className="border rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Milestone</TableHead>
                                                    <TableHead>From</TableHead>
                                                    <TableHead>To</TableHead>
                                                    <TableHead>Commission</TableHead>
                                                    <TableHead className="text-center">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {commissionMilestone.map((item, index) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>{index + 1}</TableCell>
                                                        <TableCell>
                                                            {item.isineditedmode ? (
                                                                <Input
                                                                    type="text"
                                                                    defaultValue={item.from}
                                                                    id={`from-${index}`}
                                                                    className="w-24"
                                                                />
                                                            ) : (
                                                                item.from
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.isineditedmode ? (
                                                                <Input
                                                                    type="text"
                                                                    defaultValue={item.to}
                                                                    id={`to-${index}`}
                                                                    className="w-24"
                                                                />
                                                            ) : (
                                                                item.to
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.isineditedmode ? (
                                                                <Input
                                                                    type="text"
                                                                    defaultValue={item.commission}
                                                                    id={`commission-${index}`}
                                                                    className="w-24"
                                                                />
                                                            ) : (
                                                                item.commission
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            {item.isineditedmode ? (
                                                                <div className="flex gap-2 justify-center">
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            const from = document.getElementById(`from-${index}`).value;
                                                                            const to = document.getElementById(`to-${index}`).value;
                                                                            const commission = document.getElementById(`commission-${index}`).value;
                                                                            saveData(index, from, to, commission);
                                                                        }}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        onClick={() => cancelList(index)}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex gap-2 justify-center">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleEdit(index)}
                                                                    >
                                                                        <Edit2 className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => deleteMilestone(index)}
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Create Wholesaler Dashboard - Always visible */}
                        <div className="flex items-center space-x-2 pt-4 border-t">
                            <Checkbox
                                id="createDashboard"
                                checked={createDashboard}
                                onCheckedChange={setCreateDashboard}
                            />
                            <Label htmlFor="createDashboard" className="font-normal cursor-pointer">Create Wholesaler Dashboard</Label>
                        </div>

                        {error && (
                            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                {error}
                            </div>
                        )}

                        {successMessage && (
                            <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200">
                                {successMessage}
                            </div>
                        )}

                        <div className="flex gap-2 justify-end">
                            <Button type="submit" disabled={btnLoader} className="bg-primary hover:bg-primary/90">
                                {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Create Wholesaler
                            </Button>
                            <Button type="button" variant="secondary" onClick={handleCancel} disabled={btnLoader}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default AddWholesaler;
